import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

import { IFlowData, IScore } from "../../../../../../common/types";
import { checkPlayable } from "../../../../../../common/parameters";

import { setItem, hasItem } from "../../../../../cookie";
import Icon from "../../../../../icon";

import ScoreEditor from "../../../../Score";
import { getNewScore } from "../../../../Score/utils";
import { Range } from "../../../../Form";

import EditorBar, { FlexLeft, FlexRight, Center, Changer, ChangerPopup } from "../../../EditorBar";

import messages from "./messages";

import { FlexLeftLeft, FlexLeftRight, ScoreSelector, DeleteScore } from "./styles";
import { IState, IProps } from "./interfaces";
import ChangeColor from "../../Common/ChangeColor";

class FlowEditor extends Component<IProps, IState> {
    state: IState = {
        openChanger: undefined,
        playable: false,
        page: 0,
    };
    scores: IScore[] = [];
    popup?: HTMLDivElement;
    checkPlayableInterval: ReturnType<typeof setInterval>
    constructor(props: IProps) {
        super(props);
        const { plan, taskIndex } = props;
        if (!plan) return;
        this.scores = (plan.tasks[taskIndex].data as IFlowData).scores || [];
    }
    componentDidMount() {
        this.checkPlayableInterval = setInterval(() => {
            if (this.props.inlineEditorOpen) {
                this.checkPlayable();
            }
        }, 800);
    }

    componentDidUpdate() {
        if (this.props.flowPage !== this.state.page) {
            this.setState({ page: this.props.flowPage });
        }
    }

    checkPlayable = () => {
        const playable = checkPlayable(
            {
                id: 0,
                type: "flow",
                data: {
                    scores: this.scores,
                    sizes: [],
                    scales: [],
                    color: undefined,
                },
            },
            this.props.plan
        );
        if (this.state.playable !== playable) {
            this.setState({ playable });
        }
    };

    exportScore = (scores: IScore[]) => {
        const { taskIndex, plan, editTask, post } = this.props;
        editTask({ taskIndex, changingProp: "scores", val: scores });
        post("flow-scores", {
            planId: plan._id,
            index: taskIndex,
            scores,
        });
        this.changeTempoStop();
    };

    storeScore = (score: IScore) => {
        const { page } = this.state;
        this.scores[page] = score;
    }
    changeTempo = (val: number) => {
        const { taskIndex, editTask, plan } = this.props;
        const { page } = this.state;
        const { tempos = [] } = plan.tasks[taskIndex].data as IFlowData;
        tempos[page] = val;
        editTask({ taskIndex, changingProp: "tempos", val: tempos });
    };

    changeTempoStop = () => {
        const { plan, taskIndex, post } = this.props;
        const { tempos } = plan.tasks[taskIndex].data as IFlowData;
        post("flow-tempos", {
            planId: plan._id,
            index: taskIndex,
            tempos: tempos || [],
        });
    };

    changeScale = (val: number) => {
        const { taskIndex, editTask, plan } = this.props;
        const { scales = [] } = plan.tasks[taskIndex].data as IFlowData;
        const { page } = this.state;
        scales[page] = val;
        editTask({ taskIndex, changingProp: "scales", val: scales });
    };

    changeScaleStop = () => {
        const { plan, taskIndex, post } = this.props;
        const { scales } = plan.tasks[taskIndex].data as IFlowData;
        post("flow-scales", {
            planId: plan._id,
            index: taskIndex,
            scales,
        });
    };

    changeSize = (val: number, _name?: string) => {
        const name = _name as "x" | "y";
        const { taskIndex, plan, editTask } = this.props;
        const task = plan.tasks[taskIndex];
        const { sizes = [] } = task.data as IFlowData;
        const { page } = this.state;
        let size = {
            y: sizes?.[page] ? sizes[page].y : 0,
            x: sizes?.[page] ? sizes[page].x : 0.8,
        };
        sizes[page] = size;
        sizes[page][name] = val;
        editTask({ taskIndex, changingProp: "sizes", val: sizes });
    };
    changeSizeStop = () => {
        const { taskIndex, plan, post } = this.props;
        const { sizes } = plan.tasks[taskIndex].data as IFlowData;
        post("flow-sizes", {
            planId: plan._id,
            index: taskIndex,
            sizes,
        });
    };

    changeOpen = (changer: string) => {
        setTimeout(() => {
            if (this.state.openChanger === changer) {
                window.removeEventListener("click", this.isClickedOutside);
                this.setState({ openChanger: undefined });
            } else {
                this.setState({ openChanger: changer });
                setTimeout(() => {
                    window.addEventListener("click", this.isClickedOutside);
                });
            }
        });
    };
    isClickedOutside = (e: MouseEvent) => {
        if (!this.popup!.contains(e.target as Node)) {
            this.setState({ openChanger: undefined });
            window.removeEventListener("click", this.isClickedOutside);
        }
    };
    componentWillUnmount() {
        window.removeEventListener("click", this.isClickedOutside);
        clearInterval(this.checkPlayableInterval);
    }
    changePage = (index: number) => {
        this.setState({ page: index });
        this.props.changeFlowPage(index);
    };
    addScore = () => {
        this.scores.push(getNewScore());
        this.exportScore(this.scores);
        this.setState({ page: this.scores.length - 1 }, () => {
            this.props.changeFlowPage(this.scores.length - 1);
        });
    };
    deleteScore = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
        e.stopPropagation();
        this.scores = this.scores.filter((score, i) => i !== index);
        this.exportScore(this.scores);
        if (this.scores.length - 1 < this.state.page) {
            this.setState({ page: this.scores.length - 1 });
            this.props.changeFlowPage(this.scores.length - 1);
        }
    };
    render() {
        const { openChanger, playable, page } = this.state;
        const { taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen } = this.props;
        if (!plan)
            return (
                <div>
                    <FormattedMessage {...messages.loading} />
                </div>
            );
        const task = plan.tasks[taskIndex];
        const data = task.data as IFlowData;
        const { scales = [], tempos = [], scores = [] } = data;
        let sizes: IFlowData["sizes"] = [];
        if (data.sizes) sizes = data.sizes;
        return (
            <div>
                {inlineEditorOpen && (
                    <ScoreEditor
                        taskType="flow"
                        key={page}
                        score={JSON.parse(JSON.stringify(scores[page] || {}))}
                        scale={scales[page] || 1}
                        tempo={tempos[page] || 60}
                        setScore={this.storeScore}
                        changeTempo={this.changeTempo}
                    />
                )}
                {!publicView && (
                    <EditorBar>
                        <FlexLeft flexDirection="column">
                            <FlexLeftLeft>
                                {task && task.data && (
                                    <Changer active={openChanger === "scale"}>
                                        <div
                                            onClick={e => {
                                                e.stopPropagation();
                                                this.changeOpen("scale");
                                            }}
                                        >
                                            <Icon i="scale" />
                                        </div>
                                        {openChanger === "scale" && (
                                            <ChangerPopup setRef={t => (this.popup = t)}>
                                                <Range
                                                    scale={1}
                                                    value={scales[page] || 1}
                                                    onChange={this.changeScale}
                                                    onStop={this.changeScaleStop}
                                                    min={0.8}
                                                    max={2.6}
                                                    step={0.01}
                                                    label={<FormattedMessage {...messages.scaling} />}
                                                />
                                            </ChangerPopup>
                                        )}
                                    </Changer>
                                )}
                                <Changer active={openChanger === "sizeX"}>
                                    <div
                                        onClick={e => {
                                            e.stopPropagation();
                                            this.changeOpen("sizeX");
                                        }}
                                    >
                                        <Icon i="width" />
                                    </div>
                                    {openChanger === "sizeX" && (
                                        <ChangerPopup setRef={t => (this.popup = t)}>
                                            <Range
                                                scale={1}
                                                value={sizes[page]?.x || 0.8}
                                                onChange={this.changeSize}
                                                onStop={this.changeSizeStop}
                                                min={0.5}
                                                max={0.9}
                                                step={0.01}
                                                label={<FormattedMessage {...messages.width} />}
                                                name="x"
                                            />
                                        </ChangerPopup>
                                    )}
                                </Changer>
                                <Changer active={openChanger === "sizeY"}>
                                    <div
                                        onClick={e => {
                                            e.stopPropagation();
                                            this.changeOpen("sizeY");
                                        }}
                                    >
                                        <Icon i="height" />
                                    </div>
                                    {openChanger === "sizeY" && (
                                        <ChangerPopup setRef={t => (this.popup = t)}>
                                            <Range
                                                scale={1}
                                                value={sizes[page]?.y || 120}
                                                onChange={this.changeSize}
                                                onStop={this.changeSizeStop}
                                                min={1}
                                                max={200}
                                                step={1}
                                                label={<FormattedMessage {...messages.height} />}
                                                name="y"
                                            />
                                        </ChangerPopup>
                                    )}
                                </Changer>
                            </FlexLeftLeft>
                            <FlexLeftRight>
                                <FormattedMessage {...messages.scores} />:{" "}
                                {scores.map((score, index) => (
                                    <ScoreSelector
                                        key={index}
                                        className={cn({ Active: page === index })}
                                        onClick={() => this.changePage(index)}
                                    >
                                        {index + 1}
                                        {index > 0 && (
                                            <DeleteScore onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => this.deleteScore(e, index)}>×</DeleteScore>
                                        )}
                                    </ScoreSelector>
                                ))}
                                {scores.length === 0 && (
                                    <ScoreSelector key={1} className="Active" onClick={() => this.changePage(0)}>
                                        {1}
                                    </ScoreSelector>
                                )}
                                {scores.length < 6 && (
                                    <ScoreSelector key="add" onClick={this.addScore}>
                                        +
                                    </ScoreSelector>
                                )}
                            </FlexLeftRight>
                        </FlexLeft>
                        <Center
                            disabled={(inlineEditorOpen && !playable) || !!task.ref}
                            onClick={() => {
                                toggleInlineEditor(!inlineEditorOpen);
                                this.setState({ playable: true });
                                if (inlineEditorOpen) {
                                    this.exportScore(this.scores);
                                }
                                setItem("pianoDone", "1", 60 * 60 * 24 * 365, "/");
                            }}
                            className={cn(!(inlineEditorOpen && !playable) && !hasItem("pianoDone") && "pulsate")}
                        >
                            {inlineEditorOpen ? (
                                <FormattedMessage {...messages.stopEdit} />
                            ) : (
                                <FormattedMessage {...messages.editScore} />
                            )}
                        </Center>
                        <FlexRight>
                            <Changer active={openChanger === "tempo"}>
                                <div
                                    onClick={e => {
                                        e.stopPropagation();
                                        this.changeOpen("tempo");
                                    }}
                                >
                                    <Icon i="metronome" /> <i>{tempos[page] || 60}</i>
                                </div>
                                {openChanger === "tempo" && (
                                    <ChangerPopup setRef={t => (this.popup = t)} onRight>
                                        <Range
                                            scale={1}
                                            value={tempos[page] || 60}
                                            onChange={this.changeTempo}
                                            onStop={this.changeTempoStop}
                                            min={32}
                                            max={240}
                                            step={2}
                                            label={<FormattedMessage {...messages.tempo} />}
                                        />
                                    </ChangerPopup>
                                )}
                            </Changer>
                            <ChangeColor
                                handleChangeOpen={this.changeOpen}
                                openChanger={openChanger}
                                plan={plan}
                                taskIndex={taskIndex}
                                setPopupRef={t => (this.popup = t)}
                                color={task.data?.color}
                            />
                        </FlexRight>
                    </EditorBar>
                )}
            </div>
        );
    }
}

export default FlowEditor;
